import type { AttemptStatus, CancelStage, DictationAttemptFailureType, InputMethod } from '@coursera/event-pulse-types';
import type { EventData } from '@coursera/event-pulse/react';

import type {
  SpeechRecognitionErrorCode,
  SpeechToTextError,
} from 'bundles/ai-coach-platform/components/patterns/chat/utils/VoiceInput/useSpeechToText';
import { browserNotSupportedError } from 'bundles/ai-coach-platform/components/patterns/chat/utils/VoiceInput/useSpeechToText';

type SharedCoachTrackingData = Pick<
  EventData<'dictation_attempt'>,
  'coach' | 'courseBranch' | 'courseItem' | 'enterprise' | 'product'
>;

export type DictationAttemptTrackingData = SharedCoachTrackingData & {
  attemptId: EventData<'dictation_attempt'>['attemptId'];
  attemptStatus: AttemptStatus;
  cancelStage?: CancelStage;
  failureType?: DictationAttemptFailureType;
};

export type DictationTrackingContext = Omit<
  DictationAttemptTrackingData,
  'attemptId' | 'attemptStatus' | 'cancelStage' | 'failureType'
>;

export type SendCoachMessageTrackingMetadata = Pick<EventData<'send_coach_message'>, 'inputMethod'>;

const speechRecognitionErrorMap: Partial<Record<SpeechRecognitionErrorCode, DictationAttemptFailureType>> = {
  aborted: 'aborted',
  'audio-capture': 'audio_capture',
  network: 'network',
  'no-speech': 'no_speech',
  'not-allowed': 'not_allowed',
  'service-not-allowed': 'service_not_allowed',
};

export const getInputMethod = (hasDictatedText: boolean, wasTextEdited: boolean): InputMethod => {
  if (!hasDictatedText) {
    return 'type_only';
  }

  if (wasTextEdited) {
    return 'mixed';
  }

  return 'voice_only';
};

export const mapSpeechRecognitionErrorToFailureType = (error: SpeechToTextError): DictationAttemptFailureType => {
  if (error === browserNotSupportedError) {
    return 'unknown';
  }

  return speechRecognitionErrorMap[error] ?? 'unknown';
};
